import React, { useState } from "react";
import { Lock, Inbox as InboxIcon, Mail, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Inbox() {
  const [password, setPassword] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/enquiries`, {
        headers: { "X-Inbox-Password": password },
      });
      if (!res.ok) throw new Error("unauthorized");
      const data = await res.json();
      setItems(data);
    } catch (err) {
      setError("That password did not unlock the inbox. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (items) {
    return (
      <div className="min-h-screen bg-[#0a0908] pt-32 pb-24">
        <div className="container-x max-w-4xl">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-full border border-gold/50 flex items-center justify-center">
              <InboxIcon className="text-gold" size={18} />
            </div>
            <div>
              <h1 className="font-display uppercase font-bold text-2xl md:text-3xl text-white leading-none">Enquiry Inbox</h1>
              <p className="text-white/45 text-sm mt-1">{items.length} enquir{items.length === 1 ? "y" : "ies"}, newest first</p>
            </div>
          </div>

          {items.length === 0 && (
            <p className="text-white/50">No enquiries yet.</p>
          )}

          <div className="space-y-5">
            {items.map((it) => (
              <div key={it.id} className="rounded-md border border-border bg-[#100d09] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="font-display uppercase tracking-wide text-white text-lg">{it.name}</span>
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-display px-2.5 py-1 rounded-sm border ${it.source === "concierge" ? "border-gold/50 text-gold" : "border-white/25 text-white/60"}`}>
                      {it.source === "concierge" ? "Concierge" : "Contact form"}
                    </span>
                  </div>
                  <span className="text-white/40 text-xs">{formatDate(it.created_at)}</span>
                </div>
                <a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 text-gold hover:brightness-110 text-sm mb-4">
                  <Mail size={14} /> {it.email}
                </a>
                <p className="text-white/80 leading-relaxed whitespace-pre-line">{it.message}</p>
                {it.smart_reply && (
                  <div className="mt-5 pt-4 border-t border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="text-gold" size={14} />
                      <p className="eyebrow">Note sent</p>
                    </div>
                    <p className="font-serif-accent italic text-white/70 leading-relaxed">{it.smart_reply}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center px-6 pt-24 pb-16">
      <form onSubmit={unlock} className="w-full max-w-sm bg-[#100d09] border border-border rounded-md p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-full border border-gold/50 flex items-center justify-center">
            <Lock className="text-gold" size={18} />
          </div>
          <div>
            <h1 className="font-display uppercase font-bold text-xl text-white leading-none">Enquiry Inbox</h1>
            <p className="text-white/45 text-xs mt-1">Team access only</p>
          </div>
        </div>
        <label className="eyebrow block mb-3">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border border-border rounded-sm px-4 py-3 text-white focus:outline-none focus:border-gold transition-colors"
          placeholder="Enter password"
          autoFocus
        />
        {error && <p className="text-red-400/80 text-sm mt-3">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-6 gold-gradient-bg text-[#0a0908] font-display uppercase font-bold tracking-[0.2em] text-sm py-3.5 rounded-sm hover:brightness-110 transition disabled:opacity-60"
        >
          {loading ? "Unlocking\u2026" : "Unlock"}
        </button>
      </form>
    </div>
  );
}
